import { useState, useEffect, useCallback } from "react";
import { Col, Row } from "reactstrap";

interface GalleryImage {
  fileUrl: string;
  thumbnailUrl?: string;
}

interface CarGalleryProps {
  images: GalleryImage[];
}

const CarGallery = ({ images }: CarGalleryProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const isOpen = lightboxIndex !== null;
  const total = images.length;

  const open = (idx: number) => setLightboxIndex(idx);
  const close = () => setLightboxIndex(null);
  const prev = useCallback(() => setLightboxIndex(i => i !== null ? (i - 1 + total) % total : 0), [total]);
  const next = useCallback(() => setLightboxIndex(i => i !== null ? (i + 1) % total : 0), [total]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, prev, next]);

  if (!images.length) return null;

  const thumbImages = images.slice(1, 5);
  const hiddenCount = images.length - 5;

  return (
    <>
      <Row className="g-2 g-sm-3">
        {/* Main large image */}
        <Col lg="6">
          <div
            onClick={() => open(0)}
            className="car-gallery-main"
          >
            <img
              src={images[0].fileUrl}
              alt="main"
              className="car-gallery-img"
            />
          </div>
        </Col>

        {/* Thumbnail grid */}
        {thumbImages.length > 0 && (
          <Col lg="6">
            <Row className="g-2 g-sm-3">
              {thumbImages.map((img, idx) => {
                const globalIdx = idx + 1;
                const isLastSlot = idx === 3 && hiddenCount > 0;
                return (
                  <Col xs="6" key={idx}>
                    <div
                      onClick={() => open(globalIdx)}
                      className="car-gallery-thumb"
                    >
                      <img
                        src={img.thumbnailUrl || img.fileUrl}
                        alt={`car-${globalIdx}`}
                        className="car-gallery-img"
                      />
                      {isLastSlot && (
                        <div className="car-gallery-overlay">
                          +{hiddenCount + 1} photos
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Col>
        )}
      </Row>

      {/* Lightbox */}
      {isOpen && lightboxIndex !== null && (
        <div className="car-lightbox" onClick={close}>
          {/* Close */}
          <button className="car-lightbox-close" onClick={close}>×</button>

          {/* Counter */}
          <div className="car-lightbox-counter">
            {lightboxIndex + 1} / {total}
          </div>

          {/* Main image */}
          <img
            src={images[lightboxIndex].fileUrl}
            alt="lightbox"
            className="car-lightbox-img"
            onClick={e => e.stopPropagation()}
          />

          {/* Prev */}
          {total > 1 && (
            <button
              className="car-lightbox-arrow car-lightbox-prev"
              onClick={e => { e.stopPropagation(); prev(); }}
            >
              ‹
            </button>
          )}

          {/* Next */}
          {total > 1 && (
            <button
              className="car-lightbox-arrow car-lightbox-next"
              onClick={e => { e.stopPropagation(); next(); }}
            >
              ›
            </button>
          )}

          {/* Thumbnail strip */}
          {total > 1 && (
            <div className="car-lightbox-thumbs" onClick={e => e.stopPropagation()}>
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img.thumbnailUrl || img.fileUrl}
                  alt={`thumb-${idx}`}
                  onClick={() => setLightboxIndex(idx)}
                  className={`car-lightbox-thumb-item${idx === lightboxIndex ? " active" : ""}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CarGallery;
